/**
 * Step 5 — Resumen + acciones automáticas.
 *
 * Render-only de los datos capturados en pasos previos. El click en cada
 * "Editar" salta al step correspondiente (callback al orchestrator).
 *
 * Las "acciones automáticas" son la promesa al usuario de qué pasará al
 * dar "Crear proyecto". Algunas no se implementan aún (graph init, email
 * invites) pero el wording es honesto: refleja lo que ya hace + lo que
 * está cableado.
 */

import { useTranslation } from 'react-i18next'
import {
  Check,
  Folder,
  GitFork,
  Layers,
  LayoutTemplate,
  Users,
} from 'lucide-react'

import type { Template } from '../../../api/templates'
import { type WizardState } from './types'

interface Step5Props {
  state: WizardState
  /** Catálogo de role templates (mismo array que pasamos a Step 4) —
   * lo usamos para resolver el `name` del rol seleccionado por el PM. */
  roles: Template[]
  onJumpTo: (step: WizardState['step']) => void
}

export function Step5Summary({ state, roles, onJumpTo }: Step5Props) {
  const { t } = useTranslation('projects')

  // AI-1.f.bug: lifecycle ya no es key fija — viene del fetch dinámico
  // y se guarda en state como objeto completo. Si por alguna razón
  // el user llega a Step 5 sin selección (no debería: validateStep lo
  // bloquea en Step 1), mostramos placeholder.
  const lifecycle = state.lifecycleTemplate

  // Stats de fases
  const phaseStats = Object.values(state.phases).reduce(
    (acc, mode) => {
      acc[mode] += 1
      return acc
    },
    { internal: 0, skipped: 0 } as Record<'internal' | 'skipped', number>,
  )

  return (
    <div className="p-6">
      <h3 className="font-display text-lg font-semibold mb-1 text-ink">
        {t('wizard.step5.title')}
      </h3>
      <p className="text-sm text-ink-muted font-body mb-5">
        {t('wizard.step5.subtitle')}
      </p>

      <div className="space-y-3">
        {/* Proyecto */}
        <SummaryCard
          icon={Folder}
          eyebrow={t('wizard.step5.summary.project_label')}
          onEdit={() => onJumpTo(1)}
        >
          <p className="text-sm font-medium mt-0.5 text-ink">
            {t('wizard.step5.summary.project_value', {
              name: state.name || '—',
              client: state.client || t('wizard.step5.summary.project_no_client'),
            })}
          </p>
          <p className="text-[11px] text-ink-subtle mt-0.5">
            {t('wizard.step5.summary.project_meta', {
              code: state.code || t('wizard.step5.summary.code_auto'),
              language: state.contentLanguage,
            })}
          </p>
        </SummaryCard>

        {/* Lifecycle */}
        <SummaryCard
          icon={LayoutTemplate}
          eyebrow={t('wizard.step5.summary.lifecycle_label')}
          onEdit={() => onJumpTo(1)}
        >
          <p className="text-sm font-medium mt-0.5 text-ink">
            {lifecycle?.name ?? t('wizard.step5.summary.lifecycle_unset')}
            {lifecycle ? ` · v${lifecycle.version}` : ''}
          </p>
          {lifecycle ? (
            <p className="text-[11px] text-ink-subtle mt-0.5 font-mono">
              {lifecycle.templateKey}
              {lifecycle.description ? ` · ${lifecycle.description}` : ''}
            </p>
          ) : null}
        </SummaryCard>

        {/* Metodología */}
        <SummaryCard
          icon={GitFork}
          eyebrow={t('wizard.step5.summary.methodology_label')}
          onEdit={() => onJumpTo(2)}
        >
          <p className="text-sm font-medium mt-0.5 text-ink">
            {t('wizard.step5.summary.methodology_meta', {
              methodology: t(`methodology.${state.methodology}` as const),
              sprint: t(`wizard.step2.cadence.weeks_${weeksWord(state.sprintLength)}` as const),
              release: t(
                `wizard.step2.cadence.release_${state.releaseCadence}` as const,
              ),
            })}
          </p>
          <p className="text-[11px] text-ink-subtle mt-0.5">
            {t(`wizard.step2.options.${state.methodology}.description` as const)}
          </p>
        </SummaryCard>

        {/* Phases */}
        <SummaryCard
          icon={Layers}
          eyebrow={t('wizard.step5.summary.phases_label')}
          onEdit={() => onJumpTo(3)}
        >
          <p className="text-sm font-medium mt-0.5 text-ink">
            {t('wizard.step5.summary.phases_meta_internal', {
              internal: phaseStats.internal,
            })}{' '}
            ·{' '}
            {t('wizard.step5.summary.phases_meta_skipped', {
              skipped: phaseStats.skipped,
            })}
          </p>
        </SummaryCard>

        {/* Equipo */}
        <SummaryCard
          icon={Users}
          eyebrow={t('wizard.step5.summary.team_label')}
          onEdit={() => onJumpTo(4)}
        >
          <p className="text-sm font-medium mt-0.5 text-ink">
            {t('wizard.step5.summary.team_meta_internal', {
              role:
                roles.find((r) => r.template_key === state.internalRole)
                  ?.name ?? state.internalRole,
            })}
            {' · '}
            {state.stakeholders.length === 0
              ? t('wizard.step5.summary.team_meta_no_stakeholders')
              : t('wizard.step5.summary.team_meta_stakeholders', {
                  count: state.stakeholders.length,
                })}
          </p>
        </SummaryCard>
      </div>

      {/* Acciones automáticas */}
      <div className="mt-5 bg-official-soft/40 border border-official/30 rounded-md p-4">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-official mb-2">
          {t('wizard.step5.actions_title')}
        </p>
        <ul className="text-xs space-y-1.5 font-body text-ink-muted">
          {(['snapshot', 'phases', 'invitations', 'graph'] as const).map(
            (k) => (
              <li key={k} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-official mt-0.5 shrink-0" />
                <span>{t(`wizard.step5.actions.${k}` as const)}</span>
              </li>
            ),
          )}
        </ul>
      </div>
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  eyebrow,
  children,
  onEdit,
}: {
  icon: typeof Folder
  eyebrow: string
  children: React.ReactNode
  onEdit: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <article className="bg-surface-muted border border-line rounded-md p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-md bg-brand-soft text-brand grid place-items-center shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
              {eyebrow}
            </p>
            {children}
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-brand hover:underline shrink-0"
        >
          {t('wizard.step5.edit')}
        </button>
      </div>
    </article>
  )
}

function weeksWord(n: 1 | 2 | 3 | 4): 'one' | 'two' | 'three' | 'four' {
  switch (n) {
    case 1:
      return 'one'
    case 2:
      return 'two'
    case 3:
      return 'three'
    case 4:
      return 'four'
  }
}
